import React, { useRef, useEffect, useCallback, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import { createPortal } from 'react-dom';

import config from '../../config';
import uniqueId from '../../utils/uniqueId';
import isFunction, { execIfFunction } from '../../utils/isFunction';
import { Spinner } from './Spinner';

const ACTIVE_CLASS = 'active';

/**
 * Auto-suggestion functional component.
 */
const AutoSuggestionComponent = ({
  id,
  name,
  value = '',
  className = 'form-control',
  suggestions,
  mode,
  data,
  onTextChange,
  setInputElement,
  onSuggestionSelect,
  onSuggestionClose,
  onSuggestionInputClick,
  onBlur,
  disabled,
  hasError,
  placeholder,
  autoFocus = false,
  width,
}) => {
  const menuId = useRef(uniqueId());
  const inputId = useRef(id || uniqueId());
  const inputRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    const menu = document.getElementById(menuId.current);
    const input = document.getElementById(inputId.current);

    if (menu && input) {
      const rect = input.getBoundingClientRect();

      // The menu is absolutely positioned in the body, so the page scroll is added to the viewport coordinates.
      menu.style.display = 'block';
      menu.style.left = `${rect.left + window.scrollX}px`;
      menu.style.top = `${rect.top + rect.height + window.scrollY}px`;
      menu.style.width = width ? `${width}px` : `${rect.width}px`;
      menu.style.zIndex = '1200';
    }

    return () => {
      if (menu) menu.style.display = 'none';
    };
  }, [mode, suggestions, width]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [suggestions]);

  useEffect(() => {
    const menu = document.getElementById(menuId.current);

    if (!menu) return;

    const activeItem = menu.querySelector(`.${ACTIVE_CLASS}`);

    if (activeItem) {
      activeItem.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [activeIndex]);

  const handleInputChange = useCallback(
    event => {
      execIfFunction(onTextChange, { event, data });
    },
    [onTextChange, data],
  );

  const handleInputClick = useCallback(
    event => {
      if (isFunction(onSuggestionInputClick)) {
        onSuggestionInputClick({ event });
      } else {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [onSuggestionInputClick],
  );

  const handleSuggestionSelect = useCallback(
    (event, suggestion) => {
      event.preventDefault();
      event.stopPropagation();
      execIfFunction(onSuggestionSelect, {
        event: { target: { name, value } },
        data,
        suggestion,
      });
    },
    [onSuggestionSelect, name, value, data],
  );

  const renderMenu = useCallback(
    (mode, suggestions) => {
      const empty =
        suggestions && suggestions.size === 0 && mode !== config.LOADING_MODE ? (
          <li>
            <a role="menuitem">No data</a>
          </li>
        ) : null;

      if (mode === config.SUGGEST_MODE) {
        const container = (
          <ul
            className="auto-suggestion dropdown-menu"
            id={menuId.current}
            onMouseDown={event => event.preventDefault()}
          >
            {empty}
            {suggestions.map((suggestion, index) => (
              <li key={index} className={index === activeIndex ? ACTIVE_CLASS : ''}>
                <a onClick={event => handleSuggestionSelect(event, suggestion)}>{suggestion.get('name')}</a>
              </li>
            ))}
          </ul>
        );

        return createPortal(container, document.body);
      }

      if (mode === config.LOADING_MODE) {
        const container = (
          <ul className="dropdown-menu" id={menuId.current}>
            <li className="react-autosuggest__suggestion">
              <Spinner size="small" inline /> Loading...
            </li>
          </ul>
        );

        return createPortal(container, document.body);
      }

      return null;
    },
    [handleSuggestionSelect, activeIndex],
  );

  const handleKeyDown = useCallback(
    event => {
      if (!suggestions || suggestions.size === 0) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setActiveIndex(prev => (prev < suggestions.size - 1 ? prev + 1 : 0));
          break;

        case 'ArrowUp':
          event.preventDefault();
          setActiveIndex(prev => (prev > 0 ? prev - 1 : suggestions.size - 1));
          break;

        case 'Enter':
          if (activeIndex >= 0) {
            event.preventDefault();

            const suggestion = suggestions.get(activeIndex);

            handleSuggestionSelect(event, suggestion);
          }
          break;

        case 'Escape':
          execIfFunction(onSuggestionClose, {
            event,
            data,
          });
          setActiveIndex(-1);
          break;

        default:
          break;
      }
    },
    [suggestions, activeIndex, handleSuggestionSelect, onSuggestionClose],
  );

  useEffect(() => {
    if (isFunction(setInputElement)) {
      setInputElement(inputRef.current);
    }
  }, [setInputElement]);

  const errorClass = hasError ? 'has-error' : '';

  return (
    <div className={errorClass}>
      <input
        ref={inputRef}
        autoComplete="off"
        autoFocus={autoFocus}
        className={className}
        disabled={disabled}
        id={inputId.current}
        name={name}
        onBlur={onBlur}
        onChange={handleInputChange}
        onClick={handleInputClick}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        type="text"
        value={value}
      />
      {renderMenu(mode, suggestions)}
    </div>
  );
};

AutoSuggestionComponent.propTypes = {
  className: PropTypes.string,
  data: PropTypes.object,
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,
  id: PropTypes.string,
  mode: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onSuggestionClose: PropTypes.func,
  onSuggestionInputClick: PropTypes.func,
  onSuggestionSelect: PropTypes.func,
  onTextChange: PropTypes.func,
  placeholder: PropTypes.string,
  setInputElement: PropTypes.func,
  suggestions: PropTypes.instanceOf(List).isRequired,
  value: PropTypes.string,
  width: PropTypes.number,
};

export const AutoSuggestion = memo(AutoSuggestionComponent);
